import axios from 'axios';
import { useLogto } from '@logto/react';
import { useMemo } from 'react';
import { toast } from 'react-toastify';

/**
 * A custom hook that returns a pre-configured Axios instance for making
 * authenticated API calls. It automatically handles adding the
 * access token to requests.
 */
const useApi = () => {
    const { getAccessToken } = useLogto();

    const authedApi = useMemo(() => {
        const api = axios.create({
            baseURL: process.env.REACT_APP_API_URL,
            // A 1-second timeout is quite short. Let's increase it.
            timeout: 10000,
        });

        api.interceptors.request.use(
            async (config) => {
                try {
                    // The resource should match what you have in your oidcConfig
                    const token = await getAccessToken(process.env.REACT_APP_API_URL);
                    config.headers.Authorization = `Bearer ${token}`;
                } catch (e) {
                    console.error("Failed to get access token for API request", e);
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Add the global response error handler from your old apiService
        api.interceptors.response.use(
            (response) => {
                // Any status code that lie within the range of 2xx cause this function to trigger
                return response;
            },
            (error) => {
                // Any status codes that falls outside the range of 2xx cause this function to trigger
                if (error.response) {
                    toast.error(`Error: ${error.response.data.message || error.response.statusText}`);
                } else if (error.request) {
                    toast.error('Error: No response from server');
                } else {
                    toast.error(`Error: ${error.message}`);
                }
                return Promise.reject(error);
            }
        );
        return api;
    }, [getAccessToken]);

    return authedApi;
};

export default useApi;